import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { CustomTooltip } from '@components/shared/CustomTooltip';
import { LENS_DOCS, TAG_THEME } from '@constants/analysisLenses';
import ArticleIcon from '@mui/icons-material/Article';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

const chipSx = {
  fontSize: 10,
  bgcolor: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.background || '#e3f2fd';
  },
  color: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.text || '#7d5ae8';
  },
};

export function LensCard({
  lens,
  onRun,
  disabled,
  noTranscript,
  selected,
  onSelect,
  setOpenCustomLensModal,
}) {
  const tooltipText = noTranscript
    ? 'No transcripts found. Please collect some responses to run this analysis.'
    : lens.enterprise
      ? lens.enterpriseMessage ||
        'This feature requires additional workspace access. Contact service@qualz.ai to enable.'
      : '';

  const isDisabled = disabled || lens.enterprise;

  const cardContent = (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: selected ? 'primary.light' : 'background.paper',
        p: 1,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': {
          borderColor: isDisabled ? 'divider' : 'primary.main',
        },
      }}
      onClick={() => {
        if (isDisabled) {
          // Don't allow selecting disabled lenses
          return;
        }
        if (lens.id === 'custom_lens') {
          setOpenCustomLensModal(true);
          onSelect();
        } else {
          onSelect();
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {lens.name}
            {lens.enterprise && (
              <LockOutlinedIcon sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }} />
            )}
            <AnalysisLensHelperText lensName={lens.id} level="run" size="small" />
          </Typography>
          {/* {lens.isBeta && <Chip label="Beta" size="medium" sx={chipSxBeta} />} */}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {lens.description}
        </Typography>
        {/* Documentation Links */}
        {LENS_DOCS[lens.id] && (
          <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
            {LENS_DOCS[lens.id].blog && (
              <Link
                href={LENS_DOCS[lens.id].blog}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'primary.main',
                  fontSize: '0.875rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <ArticleIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                Learn more
                {/* <OpenInNew sx={{ fontSize: '0.875rem', ml: 0.5 }} /> */}
              </Link>
            )}
            {LENS_DOCS[lens.id].userGuide && (
              <Tooltip title={`User Guide for ${lens.name}`} arrow>
                <Link
                  href={LENS_DOCS[lens.id].userGuide}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Guide
                </Link>
              </Tooltip>
            )}
          </Stack>
        )}
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {lens.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                ...chipSx,
                borderRadius: 2,
                bgcolor: TAG_THEME[tag]?.background || '#e3f2fd',
                color: TAG_THEME[tag]?.text || '#7d5ae8',
              }}
            />
          ))}
        </Stack>
      </CardContent>
      <Box sx={{ p: 1, pt: 0 }}>
        {lens.enterprise ? (
          <Button
            variant="custom"
            fullWidth
            disabled
            startIcon={<LockOutlinedIcon />}
            sx={{ opacity: 0.7 }}
          >
            Requires Access
          </Button>
        ) : (
          <Button
            variant={selected ? 'contained' : 'custom'}
            fullWidth
            disabled={isDisabled}
            onClick={() => onRun(lens)}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>
        )}
      </Box>
    </Card>
  );

  return tooltipText ? (
    <CustomTooltip arrow title={tooltipText}>
      {cardContent}
    </CustomTooltip>
  ) : (
    cardContent
  );
}
